// Libraries
import React from 'react';
// Components
import { Button, ButtonWithTooltip } from '@components/button';
import { TableElectronicPayroll, ModalMistakes } from '../../components';
import { ModalInformationImportant } from '@pages/electronic-payroll/components';
// Models
import { CurrentInformation } from '@pages/electronic-payroll/constants/InformationImportant';
// Constants
import { confirmTransmisonText, transmitText } from '../../constants/TextTooltip';
// Hooks
import { useGetActivePlan } from '@hooks/useGetActivePlan';
import { useTransmitElectronicPayroll } from '@pages/electronic-payroll/hooks/useTransmitElectronicPayroll';
import { useModalInformationImportant } from '@pages/electronic-payroll/hooks/useModalInformationImportant';
// Styles
import './TransmitElectronicPayroll.scss';
import { CarouselPeriods } from '@components/carousel';
import { ModalImportantInfo } from '@components/modal';
import { PILA_MESSAGE_ERROR } from '@pages/electronic-payroll/constants/TextModal';
import { TEXT_ELECTTRONIC_PAYROLL, TEXT_HOME } from '@pages/electronic-payroll/constants/Text';
import { useAppSelector } from '@hooks/redux';
import { RootState } from '@redux/rootReducer';
import { PARTIALLY_TRANSMITED, REFUSED, TRANSMIT } from '@pages/electronic-payroll/constants/Transmit';
import { ModalConfigureSocialBenefits } from '../configure-social-benefits/ModalConfigureSocialBenefits';

export const TransmitElectronicPayroll: React.FC = () => {
    const { userPlan } = useGetActivePlan();
    const {
        periods,
        currentDate,
        dataPayroll,
        confirmed,
        errorModal,
        transmitConfirmed,
        noveltyTransmisionError,
        dataErrorModal,
        errorGeneratePayrollModal,
        buyNewPlan,
        showSocialBenefitsModal,
        socialBenefitsConfigurationValues,
        handleGeneratePayroll,
        handleOpenModalError,
        handleOpenModalErrorGenerate,
        handleChangeCard,
        handleGetDateModal,
        handleRedirectPayrollPlan,
        setShowSocialBenefitsModal,
        handleConfirmSocialBenefitsConfiguration,
    } = useTransmitElectronicPayroll();
    const { showModalImportant, currentInformation, handleShowModalImportant, handleSelectTypeInformation, handleMainAction } =
        useModalInformationImportant(currentDate);

    const {
        electronicPayroll: { isAlreadyTransmitedThisPeriod, payroll },
    } = useAppSelector((state: RootState) => state);
    const SHOW_RETRANSMISION =
        !noveltyTransmisionError &&
        (payroll?.status === REFUSED || payroll?.status === TRANSMIT || payroll?.status === PARTIALLY_TRANSMITED);
    const RETRANSMISION_DISABLED = payroll?.status === TRANSMIT;
    const SOCIAL_BENEFITS_CONFIGURED =
        socialBenefitsConfigurationValues.service_bonus &&
        socialBenefitsConfigurationValues.severance &&
        socialBenefitsConfigurationValues.electronic_payroll_vacation_periodicity;

    const shouldDisbaleSocialBenefitButtons =
        socialBenefitsConfigurationValues.service_bonus &&
        socialBenefitsConfigurationValues.severance &&
        !socialBenefitsConfigurationValues.electronic_payroll_vacation_periodicity;

    return (
        <div className="transmit-electronic-payroll">
            <ModalInformationImportant
                isOpen={showModalImportant}
                onRequestClose={handleShowModalImportant}
                date={handleGetDateModal()}
                handleBack={(): void => handleShowModalImportant()}
                mainAction={(): Promise<void> => handleMainAction(currentInformation)}
                currentInformation={currentInformation}
            />

            <ModalConfigureSocialBenefits
                isOpen={showSocialBenefitsModal}
                socialBenefitsConfigurationValues={{
                    service_bonus: socialBenefitsConfigurationValues.service_bonus,
                    severance: socialBenefitsConfigurationValues.severance,
                    electronic_payroll_vacation_periodicity:
                        socialBenefitsConfigurationValues.electronic_payroll_vacation_periodicity,
                }}
                handleBack={(): void => setShowSocialBenefitsModal(false)}
                mainAction={handleConfirmSocialBenefitsConfiguration}
                shouldDisbaleSocialBenefitButtons={Boolean(shouldDisbaleSocialBenefitButtons)}
            />

            <ModalMistakes
                data={dataErrorModal}
                isOpen={errorModal}
                onRequestClose={handleOpenModalError}
                confirm={buyNewPlan}
                mainAction={handleRedirectPayrollPlan}
                handleBack={handleOpenModalError}
            />

            <ModalImportantInfo
                isOpen={errorGeneratePayrollModal}
                onRequestClose={handleOpenModalErrorGenerate}
                confirm={true}
                mainAction={handleOpenModalErrorGenerate}
                hiddenBackButton={true}
            >
                <>
                    <p>{PILA_MESSAGE_ERROR}</p>
                </>
            </ModalImportantInfo>

            <div className="transmit-electronic-payroll__body">
                <div className="container__link">
                    <label
                        className="link--custom"
                        onClick={(): void => {
                            handleSelectTypeInformation(CurrentInformation.CHECK_HISTORIC);
                            handleShowModalImportant();
                        }}
                    >
                        Ver histórico
                    </label>
                </div>
                <div className="body__container">
                    <p className="description">
                        {currentInformation === CurrentInformation.CONFIRM_TRANSMISSION ? TEXT_ELECTTRONIC_PAYROLL : TEXT_HOME}
                    </p>
                    <div>
                        <CarouselPeriods
                            data={periods}
                            handleChange={handleChangeCard}
                            handlePeriod={(): void => {}}
                            currentDate={currentDate}
                            currentIndex={1}
                        />
                    </div>
                    <div className="container__table">
                        <TableElectronicPayroll
                            data={transmitConfirmed ? dataPayroll : []}
                            transmit={confirmed}
                            handleModalDetail={(): void => {
                                handleSelectTypeInformation(CurrentInformation.CHECK_DETAIL);
                                handleShowModalImportant();
                            }}
                        />
                    </div>
                    <div className="container__buttons">
                        {!transmitConfirmed ? (
                            <ButtonWithTooltip
                                tooltip
                                disabled={!userPlan?.is_active || !userPlan?.electronic_payroll}
                                className={`${
                                    !userPlan?.is_active || !userPlan?.electronic_payroll
                                        ? 'container__buttons--disabled'
                                        : 'container__buttons--active'
                                }`}
                                textTooltip={transmitText}
                                tooltipPosition="top-end"
                                tooltipSize="small"
                                onClick={async (): Promise<void> => {
                                    !SOCIAL_BENEFITS_CONFIGURED ? setShowSocialBenefitsModal(true) : handleGeneratePayroll();
                                }}
                            >
                                Transmitir
                            </ButtonWithTooltip>
                        ) : !SHOW_RETRANSMISION ? (
                            <ButtonWithTooltip
                                tooltip
                                disabled={confirmed || !userPlan?.is_active || isAlreadyTransmitedThisPeriod}
                                className={`${
                                    !userPlan?.is_active || isAlreadyTransmitedThisPeriod
                                        ? 'container__buttons--disabled'
                                        : 'container__buttons--active'
                                }`}
                                textTooltip={confirmTransmisonText}
                                tooltipPosition="top-end"
                                tooltipSize="small"
                                onClick={(): void => {
                                    handleSelectTypeInformation(CurrentInformation.CONFIRM_TRANSMISSION);
                                    handleShowModalImportant();
                                }}
                            >
                                Confirmar transmisión
                            </ButtonWithTooltip>
                        ) : (
                            <Button
                                disabled={!userPlan?.is_active || RETRANSMISION_DISABLED}
                                className={`${
                                    !userPlan?.is_active || RETRANSMISION_DISABLED
                                        ? 'container__buttons--disabled'
                                        : 'container__buttons--active'
                                }`}
                                onClick={async (): Promise<void> => {
                                    handleGeneratePayroll();
                                }}
                            >
                                Retransmitir
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
